import { AdmDataGrid } from "@components/common/data-grid/adm-data-grid";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchCompanyGroups } from "@store/workspace/workspace-thunks";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { companyStateSelector } from "@store/workspace/workspace-slice";
import { Spinner } from "@components/common/spinner";
import { ContentLayout } from "@pages/content-layout";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { useWorkspaceGroupsTableColumns } from "@pages/workspace-profile/tab-contents/workspace-groups-tab/use-workspace-groups-table-columns";
import { CSVDataUtils } from "@utils/csv-data";

/** Fetch workspace groups and display them in a table */
export function WorkspaceGroups({
  companyId,
}: BaseCompanyIdProps): React.JSX.Element {
  const { handleErrorWithToast } = useErrorHandler();
  const [shouldShowErrorMessage, setShouldShowErrorMessage] =
    useState<boolean>(false);

  const errorMessage = "Could not load groups of the workspace";

  const columns = useWorkspaceGroupsTableColumns({ companyId });

  const { groups, fetchingCompany } = useAppSelector(companyStateSelector);

  const dispatch = useAppDispatch();

  // Fetches the company groups
  useEffect(() => {
    dispatch(fetchCompanyGroups({ companyId }))
      .unwrap()
      .catch((error) => {
        handleErrorWithToast({
          id: `CompanyGroups-${Date.now().toString()}`,
          title: errorMessage,
          error,
        });
        setShouldShowErrorMessage(true);
      });
  }, [companyId, dispatch, handleErrorWithToast]);

  if (fetchingCompany.companyGroups === "pending") {
    return <Spinner />;
  }

  return (
    <ContentLayout contentSx={{ overflowY: "hidden" }}>
      <AdmDataGrid
        entities={groups}
        uniqueIdKey="id"
        tableType="workspace-groups-table"
        columns={columns}
        errorMessage={errorMessage}
        shouldShowErrorMessage={shouldShowErrorMessage}
        onExport={() => CSVDataUtils.downloadGroupData(groups)}
      />
    </ContentLayout>
  );
}
